/**
 * Simple CYO
 *
 * The goal of this project is to streamline how we handle
 * the rendering of UGC based text
 *
 * monogram_location
 * monogram_type
 * monogram_customization_selection
 * monogram_text_entry
 * monogram_[location_vendor_id]_ugc
 * monogram_embroidery_color
 **/

const SimpleCYO = function (app) {

  const _ = require('lodash');
  const utils = require('./utils');
  const configure = app.configure;
  const drawBigMonogram = require('./monogramming.js').drawBigMonogram;
  const clearMonogram = utils.clearMonogram;
  const updateView = utils.updateView;
  const allowedAliasPartials = [
    '_location',
    '_type',
    '_customization_selection',
    '_text_entry',
    '_embroidery_color',
    '_ugc'
  ];
  const getSelectedValue = app.helpers.getSelectedValueByAlias;
  const loadingClass = 'fc-monogramming-display-loading';
  let canvas;

  // Takes an alias string and returns the base
  // e.g. `monogram_location` returns `monogram`
  function baseName(alias) {
    return alias.split('_')[0];
  }

  function baseAlias(alias) {
    return `${baseName(alias)}_location`;
  }

  // Takes an alias string and returns the string
  // minus the base. e.g. `monogram_location` returns `_location`
  function getAliasPartial(alias) {
    const tmpArray = alias.split('_');
    tmpArray.splice(0, 1);
    return `_${tmpArray.join('_')}`;
  }

  // Get the AV for the _location attribute
  function getLocation(alias) {
    return app.helpers.getSelectedValueByAlias(baseAlias(alias));
  }

  // If the base alias object has a child _ugc attribute
  function isUgc(base, location) {
    return (app.helpers.hasAttribute(`${base}_${location}_ugc`));
  }

  /* ----------  Main method that gets called to render the text  ---------- */
  function updateText(change) {
    const base = baseName(change.ca.alias);
    const location = getLocation(base);
    const locationName = location.vendorId;

    if (!isUgc(base, locationName)) {
      return;
    }

    app.performance.mark('configure:ugc:start');

    const ctx = canvas.getContext('2d');
    const ugcAlias = `${base}_${locationName}_ugc`;
    const initials = configure.run('getAttribute', { alias: `${base}_text_entry` }).value.text;
    const fontid = app.utils.getMetaData(
      getSelectedValue(`${base}_customization_selection`)
    ).fontid;
    const color = getSelectedValue(`${base}_embroidery_color`).color;
    const { view } = app.helpers.getMetaData(location);

    updateView(app, view);

    ctx.clearRect(0, 0, 400, 400);

    drawBigMonogram(ctx, {
      initials,
      fontid,
      color,
      absoluteParent: baseAlias(change.ca.alias),
      app
    });

    const dataUrl = canvas.toDataURL();

    // Before setting the new value clear the old values
    clearMonogram(configure, ugcAlias);

    if (!document.querySelector(loadingClass)) {
      app.model.toggleDisplayLoading();
      // Indicate status loading so we can disable the add to cart button.
      app.actions.updateMonogramUGC('loading');
      app.cart.updateAddToCart();
    }

    app.performance.mark('configure:ugc:upload-image');

    if (app.helpers.hasAttribute(ugcAlias)) {
      app.configure.run('setUgcImage', {
        ca: { alias: ugcAlias },
        dataURI: dataUrl,
        onUploadComplete(uploadedImageDetails) {
          app.actions.updateMonogramUGC(encodeURIComponent(uploadedImageDetails.av.clipArt));
          if (app.isFluidEnv() && app.mergedParams.showMonogramming) {
            console.log('ugc update done', uploadedImageDetails);
          }
        },
        setValue: true
      }, function (err) {
        if (err) {
          console.log('ugc error', err);
        }

        app.performance.mark('configure:ugc:success-callback');
      });
    }
  }

  function verifyChanges(changes, $monogramCanvas) {
    // Check to see if our allowed alias is defined
    const filteredChanges = changes.filter(change => {
      return allowedAliasPartials.indexOf(
        getAliasPartial(change.ca.alias)
      ) !== -1;
    });

    if (!filteredChanges.length) { return; }

    canvas = $monogramCanvas[0];

    filteredChanges.forEach(filteredChange => _.defer(() => updateText(filteredChange)));
  }

  return {
    baseAlias,
    verifyChanges
  };

};

module.exports = SimpleCYO;